import { ImageProps } from 'next/image';

import { MuxVideo } from '@natu/ui/Video/MuxVideo';
import { VimeoVideo } from '@natu/ui/Video/VimeoVideo';
import { YoutubeVideo } from '@natu/ui/Video/YoutubeVideo';

import {
  getAssetFromStoryblok,
  resolveStoryblokStyles,
  sbEditable,
  type SBProps,
  type Spacing,
  type StoryblokAsset,
} from '../../../../utils';

interface SBVideoProps {
  controls?: boolean;
  mtMobile?: Spacing;
  mtTablet?: Spacing;
  mtDesktop?: Spacing;
  mbMobile?: Spacing;
  mbTablet?: Spacing;
  mbDesktop?: Spacing;

  // yt
  youtubeId?: string;
  start?: string;
  end?: string;

  // vimeo
  vimeoUrl?: string;
  previewImage?: StoryblokAsset;

  // file
  file?: StoryblokAsset;
  poster?: StoryblokAsset;
  loop?: boolean;
  muted?: boolean;
  autoPlay?: boolean;
}

export const SBVideo = ({ blok }: SBProps<SBVideoProps>) => {
  const {
    // global
    controls,
    mbMobile,
    mbTablet,
    mbDesktop,
    mtMobile,
    mtTablet,
    mtDesktop,
    // yt
    youtubeId,
    start,
    end,
    // vimeo
    previewImage,
    vimeoUrl,
    // file
    file,
    poster,
    muted,
    loop,
    autoPlay,
  } = blok;

  const className = resolveStoryblokStyles({
    mt: mtMobile,
    mtTablet,
    mtDesktop,
    mb: mbMobile,
    mbTablet,
    mbDesktop,
  });

  if (youtubeId) {
    const controlsParam = controls ? 'controls=1' : 'controls=0';
    const startParam = start && `start=${start}`;
    const endParam = end && Number(start || '0') < Number(end) && `end=${end}`;

    const params = [controlsParam, startParam, endParam].filter(Boolean).join('&');

    return (
      <div className={className} {...sbEditable(blok)}>
        <YoutubeVideo videoid={youtubeId} params={params} />
      </div>
    );
  }

  const { src } = getAssetFromStoryblok(file, { type: 'video' });

  if (src) {
    return (
      <div className={className} {...sbEditable(blok)}>
        <MuxVideo
          controls={controls}
          src={src}
          posterSrc={poster?.filename}
          loop={loop}
          autoPlay={autoPlay}
          muted={muted}
        />
      </div>
    );
  }

  if (vimeoUrl) {
    const resolvedImage = getAssetFromStoryblok(previewImage) as ImageProps;

    return <VimeoVideo resolvedImage={resolvedImage} loop={loop} muted={muted} url={vimeoUrl} />;
  }

  return null;
};
